// src/pages/HomePage.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, getDoc, doc, query, where, orderBy } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import "./HomePage.css";
import "./ProductsPage.css";
import PartnersStrip from "../components/PartnersStrip";
import AIChatWidget from "../components/AIChatWidget";
import WhatsAppFloatButton from "../components/WhatsAppFloatButton";
import NewsletterSignup from "../components/NewsletterSignup";
import { DEFAULT_NAV_GROUPS } from "../constants/navCategories";
import { DEFAULT_HOME_CATEGORY_SECTION } from "../constants/homeCategorySection";

// Icons
const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <path d="m9 11 3 3L22 4"/>
  </svg>
);

const TruckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
    <circle cx="7" cy="21" r="2"/>
    <path d="M9 21h6"/>
    <circle cx="17" cy="21" r="2"/>
    <path d="M17 3v5h2a2 2 0 0 1 2 2v7"/>
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 10.26 23.77 11.25 17.77 17.25 19.24 26 12 22.77 4.76 26 6.23 17.25 0.23 11.25 8.91 10.26 12 2"/>
  </svg>
);

const PrinterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);

const PaletteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13" cy="13" r="8"/>
    <path d="M5.5 9.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0"/>
    <path d="M14 4a2 2 0 0 0 2-2"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <path d="m9 11 3 3L22 4"/>
  </svg>
);

// Fallback if custom categories are not set up yet
const FALLBACK_CATEGORIES = [
  { label: "Business Cards", slug: "business-cards", desc: "Premium finishes, fast turnaround" },
  { label: "Flyers & Leaflets", slug: "flyers", desc: "A5, A4, DL and custom sizes" },
  { label: "Posters & Banners", slug: "posters", desc: "Roll-up banners, large format" },
  { label: "Brochures", slug: "brochures", desc: "Tri-fold, bi-fold, saddle-stitched" },
  { label: "Stickers & Labels", slug: "stickers", desc: "Die-cut, vinyl, waterproof" },
  { label: "Letterheads", slug: "letterheads", desc: "Branded stationery sets" },
  { label: "Postcards", slug: "postcards", desc: "Matte, gloss, UV coating" },
  { label: "Booklets", slug: "booklets", desc: "Catalogues, programmes, magazines" },
];

const FEATURES = [
  { title: "Same-Day Dispatch", desc: "Order before 2pm for same-day dispatch on most products.", Icon: ClockIcon, colorClass: "feature-cyan" },
  { title: "Free Artwork Check", desc: "Our designers review every file before printing — no surprises.", Icon: PaletteIcon, colorClass: "feature-magenta" },
  { title: "Canada-wide Delivery", desc: "Tracked delivery across the entire Canada.", Icon: TruckIcon, colorClass: "feature-yellow" },
  { title: "Premium Materials", desc: "Industry-leading paper stocks, coatings, and inks.", Icon: StarIcon, colorClass: "feature-black" },
];

const TESTIMONIALS = [
  { name: "Sarah K.",  text: "Circuit Printing delivered 500 business cards in under 2 days. The quality is absolutely outstanding — clients always comment on them.", rating: 5 },
  { name: "James M.",  text: "We've been using Circuit for all our brochures and leaflets for over a year. Reliable, affordable, and the print quality is consistently brilliant.", rating: 5 },
  { name: "Priya R.",  text: "The banner stands arrived perfectly packed and look stunning at our events. Quick turnaround and excellent communication throughout.", rating: 5 },
];

export default function HomePage() {
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [homeCategorySection, setHomeCategorySection] = useState(DEFAULT_HOME_CATEGORY_SECTION);

  useEffect(() => {
    // Fetch categories from Sinalite API
    const fetchCategories = async () => {
      try {
        const { fetchProducts } = await import("../services/sinaliteService");
        const { categories } = await fetchProducts();
        setCategories(categories); // [{ name, slug, imageUrl, description }]
      } catch {
        setCategories([]);
      }
    };

    // Fetch hero slides from the API
    const fetchHero = async () => {
      try {
        const snap = await getDocs(collection(db, "heroSlides"));
        const slides = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (slides.length > 0) setHeroSlides(slides);
      } catch (e) {
        // Use default slides
      }
    };

    // Fetch featured products from customProducts collection
    const fetchFeatured = async () => {
      try {
        const snap = await getDocs(collection(db, "customProducts"));
        const featured = snap.docs
          .map(d => ({ id: d.id, ...d.data(), isCustom: true }))
          .filter(p => p.featured !== false && p.enabled !== false);
        setFeaturedProducts(featured);
      } catch (e) { }
    };

    const fetchHomeCategorySection = async () => {
      try {
        const setting = await getDoc(doc(db, "siteSettings", "homeCategorySection"));
        if (setting.exists()) {
          const data = setting.data();
          setHomeCategorySection({
            ...DEFAULT_HOME_CATEGORY_SECTION,
            ...data,
            cards: Array.isArray(data.cards) ? data.cards : DEFAULT_HOME_CATEGORY_SECTION.cards,
          });
        }
      } catch (e) {
        console.error("Failed to load homepage product categories.", e);
      }
    };

    fetchHero();
    fetchFeatured();
    fetchCategories();
    fetchHomeCategorySection();
  }, []);

  // Auto-advance slides
  useEffect(() => {
    if (heroSlides.length < 2) return;
    const t = setInterval(() => setCurrentSlide(i => (i + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, [heroSlides]);

  const defaultHero = {
    headline: "Print That Makes Your Brand Stand Out",
    subheading: "Premium quality printing for businesses across the Canada. Fast turnaround, unbeatable prices.",
    ctaLabel: "Shop All Products",
    ctaLink: "/products",
    bgColor: "#0d0d1a",
  };

  const activeSlide = heroSlides[currentSlide] || defaultHero;

  return (
    <div className="homepage">

      {/* ── HERO ── */}
      <section className="hero" style={{ background: activeSlide.bgColor || "#f0f8ff" }}>
        <div className="container hero-split">
          {/* Left: Text */}
          <div className="hero-left">
            <span className="hero-tag">🇨🇦 Canada's Trusted Print Partner</span>
            <h1 className="hero-headline">{activeSlide.headline || defaultHero.headline}</h1>
            <p className="hero-sub">{activeSlide.subheading || defaultHero.subheading}</p>
            <div className="hero-ctas">
              <Link to={activeSlide.ctaLink || "/products"} className="btn-primary">
                {activeSlide.ctaLabel || "Shop Now"}
              </Link>
              <Link to="/contact" className="btn-outline">
                Get a Free Quote
              </Link>
            </div>
          </div>
          {/* Right: Image */}
          <div className="hero-right">
            {activeSlide.imageUrl
              ? <img src={activeSlide.imageUrl} alt="hero" className="hero-img" />
              : <div className="hero-img-placeholder">
                <div className="hero-placeholder-inner">
                  <div className="cmyk-dots">
                    <span style={{ background: "var(--cyan)" }}></span>
                    <span style={{ background: "var(--magenta)" }}></span>
                    <span style={{ background: "var(--yellow)" }}></span>
                    <span style={{ background: "var(--black)" }}></span>
                  </div>
                  <p>Add a hero image from<br />Admin → Hero Slides</p>
                </div>
              </div>
            }
          </div>
        </div>
        {heroSlides.length > 1 && (
          <div className="hero-dots">
            {heroSlides.map((_, i) => (
              <button key={i} className={`dot ${i === currentSlide ? "active" : ""}`}
                onClick={() => setCurrentSlide(i)} />
            ))}
          </div>
        )}
      </section>

      {/* ── FEATURES STRIP ── */}
      <section className="features-strip">
        <div className="container features-grid">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`feature-card ${f.colorClass} reveal delay-${i + 1}`}>
              <div className="feature-icon"><f.Icon /></div>
              <div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CATEGORIES ── */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header reveal">
            {homeCategorySection.tag && <span className="tag">{homeCategorySection.tag}</span>}
            <h2 className="section-title">{homeCategorySection.title}</h2>
            <p className="section-subtitle">{homeCategorySection.subtitle}</p>
          </div>
          <div className="categories-grid">
            {homeCategorySection.cards.map((card, i) => {
              const repCategory = categories.find(cat => card.imageCategoryNames?.includes(cat.name));
              const imageUrl = card.imageUrl || repCategory?.imageUrl;
              const link = card.link || "/products";
              return (
                <Link to={link} key={card.id || `${card.label}-${i}`} className={`cat-card reveal delay-${(i % 6) + 1}`}>
                  <div className="cat-img-wrap">
                    {imageUrl
                      ? <img src={imageUrl} alt={card.label} className="cat-img" />
                      : <div className="cat-img-fallback"><PrinterIcon /></div>
                    }
                  </div>
                  <h3 className="cat-name">{card.label}</h3>
                </Link>
              );
            })}
          </div>
          <div style={{ textAlign: "center", marginTop: 32 }} className="reveal">
            <Link to="/products" className="btn-primary" style={{ padding: "14px 36px", fontSize: 15 }}>
              View All Products →
            </Link>
          </div>
        </div>
      </section>

      {/* ── FEATURED PROMOTIONAL PRODUCTS ── */}
      {featuredProducts.length > 0 && (
        <section className="categories-section">
          <div className="container">
            <div
              className="section-header reveal"
              style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", textAlign: "left", flexWrap: "wrap", gap: 16 }}
            >
              <div>
                <span className="tag">Featured</span>
                <h2 className="section-title">Promotional Products</h2>
                <p className="section-subtitle" style={{ margin: 0 }}>Hand-picked promotional items to help your brand stand out.</p>
              </div>
              <Link
                to={DEFAULT_NAV_GROUPS.find(g => g.label === "Promotions & Apparel")?.groupLink || "/products"}
                className="btn-outline"
                style={{ whiteSpace: "nowrap" }}
              >
                View All →
              </Link>
            </div>
            <div
              style={{
                display: "flex",
                gap: 22,
                overflowX: "auto",
                paddingBottom: 12,
                scrollSnapType: "x mandatory",
              }}
            >
              {featuredProducts.map((p, i) => (
                <Link
                  to={`/products/custom/${p.id}`}
                  key={p.id}
                  className={`product-card-main reveal delay-${(i % 6) + 1}`}
                  style={{ flex: "0 0 300px", scrollSnapAlign: "start" }}
                >
                  <div className="product-img-wrap img-zoom">
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt={p.name} />
                      : <div className="product-img-placeholder"><PrinterIcon /></div>
                    }
                  </div>
                  <div className="product-card-body">
                    <span className="product-category-label">{p.category}</span>
                    <h3>{p.name}</h3>
                    {p.description && <p className="product-desc">{p.description}</p>}
                    <div className="product-card-footer">
                      <span className="btn-primary" style={{ fontSize: 13, padding: "8px 18px" }}>Order Now →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── WHY CIRCUIT ── */}
      <section className="why-section">
        <div className="container why-inner">
          <div className="why-text reveal-left">
            <span className="tag">Why Choose Us</span>
            <h2 className="section-title">Printing Done Right,<br />Every Single Time</h2>
            <p className="section-subtitle">
              We combine cutting-edge printing technology with meticulous quality control
              to deliver print materials that represent your brand at its very best.
            </p>
            <ul className="why-list">
              <li><span className="check"><CheckIcon /></span> ISO-certified printing facility</li>
                            <li><span className="check"><CheckIcon /></span> Eco-friendly paper options</li>
              <li><span className="check"><CheckIcon /></span> Dedicated account managers</li>
              <li><span className="check"><CheckIcon /></span> 100% satisfaction guarantee</li>
            </ul>
            <Link to="/about" className="btn-primary" style={{ marginTop: 28, display: "inline-block" }}>
              Learn More About Us
            </Link>
          </div>
          <div className="why-visual reveal-right">
            <div className="why-card-big">
              <div className="why-cmyk">
                <div style={{ background: "var(--cyan)" }}>C</div>
                <div style={{ background: "var(--magenta)" }}>M</div>
                <div style={{ background: "var(--yellow)", color: "#333" }}>Y</div>
                <div style={{ background: "var(--black)" }}>K</div>
              </div>
              <p className="why-card-title">CMYK Precision Printing</p>
              <p className="why-card-sub">True-to-life colours, guaranteed</p>
            </div>
            <div className="why-stats-col">
              <div className="stat-box"><strong>25+</strong><span>Years in Print</span></div>
              <div className="stat-box"><strong>500+</strong><span>Products Available</span></div>
              <div className="stat-box accent"><strong>1-Day</strong><span>Turnaround Option</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header center reveal">
            <span className="tag">What Our Clients Say</span>
            <h2 className="section-title">Trusted by Canadian Companies</h2>
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} className={`testimonial-card reveal delay-${i + 1}`}>
                <div className="stars">{"★".repeat(t.rating)}</div>
                <p>"{t.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{t.name[0]}</div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.company}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARTNERS ── */}
      <PartnersStrip />

      {/* ── NEWSLETTER SIGNUP ── */}
      <NewsletterSignup />

      {/* ── CTA BANNER ── */}
      <section className="cta-banner">
        <div className="container cta-banner-inner reveal-zoom">
          <h2>Ready to Get Your Printing Started?</h2>
          <p>Upload your artwork or request a free custom quote today.</p>
          <div className="cta-banner-btns">
            <Link to="/products" className="btn-primary">Browse Products</Link>
            <Link to="/contact" className="btn-outline">Request a Quote</Link>
          </div>
        </div>
      </section>


      {/* ── AI CHAT WIDGET ── */}
      <AIChatWidget />
      <WhatsAppFloatButton />

    </div>
  );
}